'use client';

import { useMemo } from 'react';
import useDriftClientIsReady from './useDriftClientIsReady';
import useDriftClient from './useDriftClient';
import {
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@drift-labs/sdk';
import NumLib from 'src/utils/NumLib';
import useWalletIsConnected from './useWalletIsConnected';
import { getMarketFees } from 'src/utils/market';
import useAccountExists from './useAccountExists';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { ENUM_UTILS } from '@drift/common';
import { ZERO_FEES_CONFIG_BY_MARKET_INDEX } from 'src/constants/zeroFees';
import { useCurrentUserMarginMode } from './user/useCurrentUserMarginMode';

// const DEFAULT_STATE = {
// 	tradeFee: BigNum.zero(PRICE_PRECISION_EXP),
// 	makerFeePct: 0,
// 	makerFeeBps: -Env.defaultMakerRebateBps,
// 	takerFeePct: 0,
// 	takerFeeBps: Env.defaultTakerFeeBps,
// };

/**
 * Calculates maker or taker fee for a given order quote size in a market
 * TradeFee will return a rebate if post only, fee if not
 * This should be moved to common-ts soon
 *
 * @param quoteSize
 * @param marketType
 * @param selectedMarket
 * @param isPostOnly
 * @returns
 */
export default function useCalculateTradeFee({
	quoteSize,
	marketType,
	marketIndex,
	isPostOnly,
	enteringHighLeverageMode,
}: {
	quoteSize: number;
	marketType: MarketType;
	marketIndex: number;
	isPostOnly?: boolean;
	enteringHighLeverageMode?: boolean;
}) {
	const connected = useWalletIsConnected();
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftClient();
	const userAccountExists = useAccountExists();

	const userAccountClient =
		connected &&
		driftClientIsReady &&
		driftClient &&
		userAccountExists &&
		driftClient.hasUser()
			? driftClient.getUser()
			: undefined;

	// Track marginMode to trigger re-calculation when high leverage mode changes
	const marginMode = useCurrentUserMarginMode();

	const {
		makerFeeMultiplier,
		makerFeeBps,
		makerFeePct,
		takerFeeBps,
		takerFeePct,
	} = useMemo(() => {
		let marketFees: ReturnType<typeof getMarketFees>;
		try {
			if (!userAccountClient || !driftClient) {
				marketFees = {
					makerFeeMultiplier: 0,
					makerFeePct: 0,
					makerFeeBps: -Env.defaultMakerRebateBps,
					takerFeeMultiplier: 0,
					takerFeePct: 0,
					takerFeeBps: Env.defaultTakerFeeBps,
				};
				return marketFees;
			}
			marketFees = getMarketFees(
				userAccountClient,
				marketType,
				marketIndex,
				driftClient
			);
		} catch (err) {
			// Temp solution for error on delegate account
			marketFees = {
				makerFeeMultiplier: 0,
				makerFeePct: 0,
				makerFeeBps: -Env.defaultMakerRebateBps,
				takerFeeMultiplier: 0,
				takerFeePct: 0,
				takerFeeBps: Env.defaultTakerFeeBps,
			};
		}
		return marketFees;
	}, [userAccountClient, marketType, marketIndex, driftClient, marginMode]); // marginMode is added here as a dependency to trigger a re-calculation when the user's margin mode changes

	const tradeFee = useMemo(() => {
		if (!driftClient || !driftClient._isSubscribed) {
			return BigNum.zero(PRICE_PRECISION_EXP);
		}

		if (!userAccountClient) {
			// If no user client, fall back to using driftClient's generic market fees
			const takerFeeMultiplier = driftClient.getMarketFees(
				MarketType.PERP,
				marketIndex
			).takerFee;

			const feeAmountNum = takerFeeMultiplier * quoteSize;

			return BigNum.fromPrint(feeAmountNum.toString(), QUOTE_PRECISION_EXP);
		}

		if (ENUM_UTILS.match(marketType, MarketType.SPOT)) {
			const quoteBigNum = BigNum.fromPrint(`${quoteSize}`, QUOTE_PRECISION_EXP);

			if (isPostOnly) {
				return BigNum.fromPrint(
					`${quoteSize * (Math.abs(makerFeePct) / 100)}`,
					PRICE_PRECISION_EXP
				);
			} else {
				return quoteBigNum.scale(takerFeeBps, 10_000);
			}
		}

		try {
			if (ZERO_FEES_CONFIG_BY_MARKET_INDEX[marketIndex]?.isActive) {
				return BigNum.zero(PRICE_PRECISION_EXP);
			}

			if (isPostOnly) {
				return BigNum.fromPrint(
					`${quoteSize * (Math.abs(makerFeePct) / 100)}`,
					PRICE_PRECISION_EXP
				);
			} else {
				return BigNum.from(
					userAccountClient.calculateFeeForQuoteAmount(
						NumLib.formatNum.toQuoteBN(quoteSize),
						marketIndex,
						enteringHighLeverageMode
					),
					QUOTE_PRECISION_EXP
				).shiftTo(PRICE_PRECISION_EXP);
			}
		} catch (err) {
			// Temp solution for error on delegate account
			return BigNum.zero(PRICE_PRECISION_EXP);
		}
	}, [
		driftClient,
		driftClientIsReady,
		userAccountClient,
		isPostOnly,
		quoteSize,
		marketIndex,
		marketType,
		makerFeeMultiplier,
		makerFeePct,
		enteringHighLeverageMode,
	]);

	return {
		tradeFee,
		makerFeePct,
		makerFeeBps,
		takerFeePct,
		takerFeeBps,
	};
}
